import { InteractionRequiredAuthError, BrowserAuthError } from "@azure/msal-browser";
import { hydrateObjects, cleanUpJsonForTransport } from './json-helper';
import { AccessTokenHelper } from './access-token-helper';

export class ApiClient {
    constructor(instance, inProgress, accounts) {
        this.instance = instance;
        this.inProgress = inProgress;
        this.accounts = accounts;
        this.accessTokenService = new AccessTokenHelper();
    }

    /**
     * Creates a request configuration for API calls
     * @param {string[]} scopes - The scopes required for the API call
     * @returns {Object} The access token request configuration
     */
    createAccessTokenRequest(scopes) {
        // Let AccessTokenHelper fall back to MSAL's active account
        // instead of pinning to accounts[0], which can be wrong
        // when multiple accounts are present.
        return {
            scopes
        };
    }

    /**
     * Makes an authenticated API call
     * @param {string} method - The HTTP method (GET, POST, PUT, PATCH, DELETE)
     * @param {string} url - The URL to call
     * @param {Object} body - The request body (for POST, PUT, PATCH)
     * @param {string[]} scopes - The scopes required for the API call
     * @returns {Promise<any>} The response data
     */
    async callApi(method, url, body = null, scopes = null) {

        if (!scopes){
            throw new Error("No scopes provided");
        }

        const accessTokenRequest = this.createAccessTokenRequest(scopes);


        const accessTokenResponse = await this.accessTokenService.getAccessToken(
            this.instance,
            this.inProgress,
            accessTokenRequest
        );

        if (!accessTokenResponse) {
            throw new Error("No access token");
        }

        const bearer = `Bearer ${accessTokenResponse.accessToken}`;
        const headers = new Headers();
        headers.append("Authorization", bearer);
        headers.append("Content-Type", "application/json");

        const options = {
            method,
            headers
        };

        if (body) {
            options.body = JSON.stringify(cleanUpJsonForTransport(body));
        }

        const response = await fetch(url, options);

        if (!response.ok) {
            const error = new Error(`HTTP Error: ${response.status} ${response.statusText}`);
            error.status = response.status;
            error.statusText = response.statusText;
            error.response = response;
            throw error;
        }

        const data = await response.json();
        const isArray = Array.isArray(data);
        const emptyContainer = isArray ? [] : {};
        return cleanUpJsonForTransport(Object.assign(emptyContainer, hydrateObjects(data)));
    }

    /**
     * Makes a GET request
     * @param {string} url - The URL to call
     * @param {string[]} scopes - The scopes required for the API call
     * @returns {Promise<any>} The response data
     */
    async get(url, scopes) {
        return this.callApi("GET", url, null, scopes);
    }

    /**
     * Makes a POST request
     * @param {string} url - The URL to call
     * @param {Object} body - The request body
     * @param {string[]} scopes - The scopes required for the API call
     * @returns {Promise<any>} The response data
     */
    async post(url, body, scopes) {
        return this.callApi("POST", url, body, scopes);
    }

    /**
     * Makes a PUT request
     * @param {string} url - The URL to call
     * @param {Object} body - The request body
     * @param {string[]} scopes - The scopes required for the API call
     * @returns {Promise<any>} The response data
     */
    async put(url, body, scopes) {
        return this.callApi("PUT", url, body, scopes);
    }

    /**
     * Makes a PATCH request
     * @param {string} url - The URL to call
     * @param {Object} body - The request body
     * @param {string[]} scopes - The scopes required for the API call
     * @returns {Promise<any>} The response data
     */
    async patch(url, body, scopes) {
        return this.callApi("PATCH", url, body, scopes);
    }

    /**
     * Makes a DELETE request
     * @param {string} url - The URL to call
     * @param {string[]} scopes - The scopes required for the API call
     * @returns {Promise<any>} The response data
     */
    async delete(url, scopes) {
        return this.callApi("DELETE", url, null, scopes);
    }

    /**
     * Handles authentication errors
     * @param {Error} error - The error to handle
     */
    handleAuthError(error) {
        if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
            // this.instance.acquireTokenRedirect(this.accessTokenRequest);
            console.log("Authentication error:", error);
        }
        throw error;
    }
}